import { useEffect, useRef } from 'react';

/**
 * AutoScroller — infinite CSS-transform ticker (no scrollLeft).
 * Uses a translate animation approach so no scroll events fire.
 * Pauses on hover/touch, resumes cleanly.
 */
export default function AutoScroller({ items = [], itemRenderer, className = '', rowClass = '' }) {
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const SPEED = 0.5; // px per frame
    const RESUME_DELAY = 1200;
    let resumeTimer = null;

    function tick() {
      if (!pausedRef.current) {
        posRef.current -= SPEED;
        // Each "set" is half the total track width (we render items twice)
        const halfWidth = track.scrollWidth / 2;
        if (Math.abs(posRef.current) >= halfWidth) {
          posRef.current = 0;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    const container = track.parentElement;

    const pause = () => {
      pausedRef.current = true;
      clearTimeout(resumeTimer);
    };
    const resume = () => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { pausedRef.current = false; }, RESUME_DELAY);
    };

    if (container) {
      container.addEventListener('mouseenter', pause);
      container.addEventListener('mouseleave', resume);
      container.addEventListener('touchstart', pause, { passive: true });
      container.addEventListener('touchend', resume);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resumeTimer);
      if (container) {
        container.removeEventListener('mouseenter', pause);
        container.removeEventListener('mouseleave', resume);
        container.removeEventListener('touchstart', pause);
        container.removeEventListener('touchend', resume);
      }
    };
  }, [items]);

  // Render items twice for seamless loop — use index suffix to avoid key collision
  const firstSet = items.map((item, i) => itemRenderer(item, `a-${i}`));
  const secondSet = items.map((item, i) => itemRenderer(item, `b-${i}`));

  return (
    <div className={rowClass} style={{ overflow: 'hidden' }}>
      <div
        ref={trackRef}
        style={{ display: 'flex', gap: '34px', alignItems: 'center', willChange: 'transform' }}
      >
        {firstSet}
        {secondSet}
      </div>
    </div>
  );
}
